import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Table,
  Tag,
  Row,
  Col,
  Statistic,
  DatePicker,
  Drawer,
  Descriptions,
  Spin,
  Empty,
  Alert,
  Button,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import TeamAttendanceService from "Services/EmployeeServices/TeamAttendanceService";
import { STATUS_CONFIG } from "Constants/Enums/Attendance/AttendanceStatusEnum";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Supervisor APIs return status as a string ("Present", "Absent", "Excellent", ...).
const statusColor = {
  Present: "green",
  Overtime: "green",
  Absent: "red",
  Incomplete: "orange",
  ShortTime: "orange",
  HalfDay: "gold",
  OnLeave: "blue",
  Weekend: "default",
  Holiday: "purple",
  // Work-life balance statuses
  Excellent: "green",
  Acceptable: "blue",
  Poor: "red",
  Overworked: "volcano",
};

// Attendance status comes back as a number (AttendanceStatusEnum). Map it to the
// shared STATUS_CONFIG (label + colour); fall back to a string if the API sends one.
const attendanceStatusTag = (s) => {
  if (s === null || s === undefined || s === "") return <Tag>-</Tag>;
  const cfg = STATUS_CONFIG[s];
  if (cfg) return <Tag color={cfg.color}>{cfg.name}</Tag>;
  return <Tag color={statusColor[s] || "default"}>{s}</Tag>;
};

// Work-life balance label derived from the difference % (the API's status number
// doesn't reliably match our enum, so compute it from the actual metric).
const wlbDiff = (o) =>
  o?.differencePercent ??
  (o?.expectedHours ? ((o.workedHours - o.expectedHours) / o.expectedHours) * 100 : null);

const wlbFromDiff = (diff) => {
  if (diff === null || diff === undefined) return <Tag>-</Tag>;
  if (diff > 10) return <Tag color="volcano">Overworked</Tag>;
  if (diff < -10) return <Tag color="red">Poor</Tag>;
  if (Math.abs(diff) <= 5) return <Tag color="green">Excellent</Tag>;
  return <Tag color="blue">Acceptable</Tag>;
};

// Absence rate is a "lower is better" metric, so colour it by severity (high =
// red). The old logic only went red when above baseline, which wrongly left a
// 100% absence rate green when it equalled a 100% baseline.
const absenceRateColor = (rate) => {
  if (rate == null) return undefined;
  if (rate >= 50) return "#cf1322"; // severe
  if (rate >= 20) return "#d46b08"; // elevated
  return "#389e0d"; // low
};

const fmtWorked = (s) => {
  if (!s || typeof s !== "string") return "-";
  const [h, m] = s.split(":");
  return `${Number(h)}h ${Number(m)}m`;
};
const fmtTime = (iso) => (iso ? moment(iso).format("h:mm A") : "-");

const flagTags = (r) =>
  r.isLate || r.isEarlyLeave ? (
    <>
      {r.isLate && <Tag color="orange">Late</Tag>}
      {r.isEarlyLeave && <Tag color="gold">Early</Tag>}
    </>
  ) : (
    <Text type="secondary">-</Text>
  );

const TeamAttendance = () => {
  const {
    loading,
    fetchToday,
    fetchTeamMonth,
    fetchTeamWLB,
    fetchEmployeeAttendance,
    fetchEmployeeWLB,
    fetchTeamInsights,
  } = TeamAttendanceService();

  const [today, setToday] = useState(null);

  const [monthDate, setMonthDate] = useState(dayjs());
  const [teamMonth, setTeamMonth] = useState(null);

  const [wlbDate, setWlbDate] = useState(dayjs());
  const [teamWlb, setTeamWlb] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empRecords, setEmpRecords] = useState(null);
  const [empWlb, setEmpWlb] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("today");
  const [insightsRange, setInsightsRange] = useState([dayjs().subtract(6, "day"), dayjs()]);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const loadTeamMonth = (m) =>
    fetchTeamMonth(m.month() + 1, m.year())
      .then(setTeamMonth)
      .catch(() => setTeamMonth(null));
  const loadTeamWlb = (m) =>
    fetchTeamWLB(m.month() + 1, m.year())
      .then(setTeamWlb)
      .catch(() => setTeamWlb(null));

  const loadInsights = (range) => {
    if (!range || !range[0] || !range[1]) return;
    setInsightsLoading(true);
    setInsights(null);
    fetchTeamInsights(range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD"))
      .then(setInsights)
      .catch(() => setInsights(null))
      .finally(() => setInsightsLoading(false));
  };

  // Lazy-load AI insights the first time the manager opens that tab so we don't
  // fire an AI call on every page load.
  const onTabChange = (key) => {
    setActiveTab(key);
    if (key === "insights" && !insights && !insightsLoading) loadInsights(insightsRange);
  };

  useEffect(() => {
    const now = dayjs();
    fetchToday().then(setToday).catch(() => {});
    loadTeamMonth(now);
    loadTeamWlb(now);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEmployee = async (emp) => {
    setSelectedEmp(emp);
    setEmpRecords(null);
    setEmpWlb(null);
    setDrawerOpen(true);
    try {
      setEmpLoading(true);
      const start = moment().startOf("month").format("YYYY-MM-DD");
      const end = moment().endOf("month").format("YYYY-MM-DD");
      const [att, wlb] = await Promise.all([
        fetchEmployeeAttendance(emp.employeeId, "Month", start, end).catch(() => null),
        fetchEmployeeWLB(emp.employeeId, moment().month() + 1, moment().year()).catch(() => null),
      ]);
      setEmpRecords(att);
      setEmpWlb(wlb);
    } finally {
      setEmpLoading(false);
    }
  };

  // ---- Today tab ----
  const todayColumns = [
    { title: "Employee", key: "emp", render: (_, r) => r.fullName || "-" },
    { title: "Department", dataIndex: "departmentName", key: "dept", render: (v) => v || "-" },
    { title: "Job Title", dataIndex: "jobTitleName", key: "jt", render: (v) => v || "-" },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => attendanceStatusTag(v) },
    { title: "Check In", key: "in", render: (_, r) => fmtTime(r.firstCheckIn) },
    { title: "Check Out", key: "out", render: (_, r) => fmtTime(r.lastCheckOut) },
    { title: "Worked", dataIndex: "totalWorkedHours", key: "worked", render: (v) => fmtWorked(v) },
    { title: "Flags", key: "flags", render: (_, r) => flagTags(r) },
    {
      title: "",
      key: "action",
      render: (_, r) => (
        <Button type="link" onClick={() => openEmployee(r)}>
          View
        </Button>
      ),
    },
  ];

  // ---- Monthly tab (per-day rows, expand to employees) ----
  const monthDayColumns = [
    { title: "Date", key: "date", render: (_, r) => moment(r.date).format("ddd, DD MMM") },
    { title: "Present", dataIndex: "totalPresent", key: "p" },
    { title: "Absent", dataIndex: "totalAbsent", key: "a" },
    { title: "On Leave", dataIndex: "totalOnLeave", key: "l" },
    { title: "Late", dataIndex: "totalLate", key: "lt" },
    {
      title: "Day",
      key: "daytype",
      render: (_, r) =>
        r.isHoliday ? (
          <Tag color="purple">Holiday</Tag>
        ) : r.isWeekend ? (
          <Tag>Weekend</Tag>
        ) : (
          <Tag color="blue">Working</Tag>
        ),
    },
  ];
  const monthEmployeeColumns = [
    { title: "Employee", key: "emp", render: (_, r) => r.fullName || "-" },
    { title: "Department", dataIndex: "departmentName", key: "dept", render: (v) => v || "-" },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => attendanceStatusTag(v) },
    { title: "Worked", dataIndex: "totalWorkedHours", key: "worked", render: (v) => fmtWorked(v) },
    { title: "Flags", key: "flags", render: (_, r) => flagTags(r) },
  ];

  // ---- Work-Life Balance tab ----
  const wlbColumns = [
    {
      title: "Employee",
      key: "emp",
      render: (_, r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || "-",
    },
    { title: "Working Days", dataIndex: "workingDays", key: "wd" },
    { title: "Expected Hrs", dataIndex: "expectedHours", key: "eh" },
    { title: "Worked Hrs", dataIndex: "workedHours", key: "wh" },
    {
      title: "Difference",
      dataIndex: "differencePercent",
      key: "diff",
      render: (v) => (v == null ? "-" : `${v > 0 ? "+" : ""}${v}%`),
    },
    { title: "Status", key: "status", render: (_, r) => wlbFromDiff(wlbDiff(r)) },
  ];

  // ---- AI Insights tab ----
  const agg = insights?.requestPayload?.teamAggregates;
  const baseline = insights?.requestPayload?.baseline;
  const aiInsight = insights?.insight;
  const perEmployee = insights?.requestPayload?.perEmployee;
  const flagColumns = [
    { title: "Employee", dataIndex: "employeeName", key: "emp", render: (v) => v || "-" },
    { title: "Department", dataIndex: "department", key: "dept", render: (v) => v || "-" },
    { title: "Observation", dataIndex: "observation", key: "obs", render: (v) => v || "-" },
    {
      title: "Suggested Action",
      dataIndex: "suggestedAction",
      key: "act",
      render: (v) => v || "-",
    },
  ];
  const perEmployeeColumns = [
    { title: "Employee", dataIndex: "employeeName", key: "pe-emp", render: (v) => v || "-" },
    { title: "Department", dataIndex: "department", key: "pe-dept", render: (v) => v || "-" },
    { title: "Absent (days)", dataIndex: "absent", key: "pe-absent", render: (v) => v ?? 0 },
    { title: "Late (days)", dataIndex: "late", key: "pe-late", render: (v) => v ?? 0 },
    {
      title: "Early Leave (days)",
      dataIndex: "earlyLeave",
      key: "pe-early",
      render: (v) => v ?? 0,
    },
  ];

  const tabItems = [
    {
      key: "today",
      label: "Today",
      children: (
        <>
          {today && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Present"
                    value={today.totalPresent ?? 0}
                    valueStyle={{ color: "#389e0d" }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Absent"
                    value={today.totalAbsent ?? 0}
                    valueStyle={{ color: "#cf1322" }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="On Leave"
                    value={today.totalOnLeave ?? 0}
                    valueStyle={{ color: "#1677ff" }}
                    prefix={<CalendarOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Late"
                    value={today.totalLate ?? 0}
                    valueStyle={{ color: "#d46b08" }}
                    prefix={<ClockCircleOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          )}
          <Table
            className="custom-table"
            rowKey={(r) => r.id || r.employeeId}
            loading={loading}
            columns={todayColumns}
            dataSource={today?.employees || []}
            pagination={false}
            scroll={{ x: 900 }}
            locale={{ emptyText: "No direct reports found" }}
          />
        </>
      ),
    },
    {
      key: "monthly",
      label: "Monthly",
      children: (
        <>
          <DatePicker
            picker="month"
            value={monthDate}
            allowClear={false}
            disabledDate={(d) => d && d.isAfter(dayjs(), "month")}
            onChange={(d) => {
              if (d) {
                setMonthDate(d);
                loadTeamMonth(d);
              }
            }}
            style={{ marginBottom: 16 }}
          />
          <Table
            className="custom-table"
            rowKey={(r) => r.date}
            loading={loading}
            columns={monthDayColumns}
            dataSource={teamMonth?.records || []}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 700 }}
            locale={{ emptyText: "No attendance for this month" }}
            expandable={{
              expandedRowRender: (rec) => (
                <Table
                  size="small"
                  rowKey={(r) => `${rec.date}-${r.employeeId}`}
                  columns={monthEmployeeColumns}
                  dataSource={rec.employees || []}
                  pagination={false}
                />
              ),
            }}
          />
        </>
      ),
    },
    {
      key: "wlb",
      label: "Work-Life Balance",
      children: (
        <>
          <DatePicker
            picker="month"
            value={wlbDate}
            allowClear={false}
            disabledDate={(d) => d && d.isAfter(dayjs(), "month")}
            onChange={(d) => {
              if (d) {
                setWlbDate(d);
                loadTeamWlb(d);
              }
            }}
            style={{ marginBottom: 16 }}
          />
          <Table
            className="custom-table"
            rowKey={(r) => r.employeeId}
            loading={loading}
            columns={wlbColumns}
            dataSource={teamWlb?.employees || []}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 700 }}
            locale={{ emptyText: "No work-life balance data for this month" }}
          />
        </>
      ),
    },
    {
      key: "insights",
      label: "AI Insights",
      children: (
        <>
          <div style={{ marginBottom: 16 }}>
            <RangePicker
              value={insightsRange}
              allowClear={false}
              disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
              onChange={(r) => {
                if (r && r[0] && r[1]) {
                  setInsightsRange(r);
                  loadInsights(r);
                }
              }}
            />
          </div>

          {insightsLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : !insights ? (
            <Empty description="Select a date range to view insights" />
          ) : (
            <>
              {agg && agg.totalEmployees > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={8}>
                    <Card size="small">
                      <Statistic title="Employees" value={agg.totalEmployees ?? 0} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Card size="small">
                      <Statistic
                        title="Absent (days)"
                        value={agg.absentCount ?? 0}
                        valueStyle={{ color: "#cf1322" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Card size="small">
                      <Statistic
                        title="Late (days)"
                        value={agg.lateCount ?? 0}
                        valueStyle={{ color: "#d46b08" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Card size="small">
                      <Statistic
                        title="Early Leave (days)"
                        value={agg.earlyLeaveCount ?? 0}
                        valueStyle={{ color: "#d4b106" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Card size="small">
                      <Statistic
                        title="Absence Rate"
                        value={agg.absenceRatePct ?? 0}
                        suffix="%"
                        valueStyle={{ color: absenceRateColor(agg.absenceRatePct) }}
                      />
                      {baseline && baseline.absenceRatePct != null && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Baseline {baseline.absenceRatePct}%
                        </Text>
                      )}
                    </Card>
                  </Col>
                </Row>
              )}

              {perEmployee && perEmployee.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Per-employee breakdown</Text>
                  <Table
                    className="custom-table"
                    style={{ marginTop: 8 }}
                    size="small"
                    columns={perEmployeeColumns}
                    dataSource={perEmployee.map((e, i) => ({ ...e, key: i }))}
                    pagination={false}
                    scroll={{ x: 600 }}
                  />
                </div>
              )}

              {insights.isAiInsightAvailable && aiInsight ? (
                <>
                  <Card
                    size="small"
                    style={{
                      marginBottom: 16,
                      background: "#f6faf6",
                      border: "1px solid #d9f7be",
                    }}
                  >
                    <Text strong style={{ fontSize: 16, display: "block", marginBottom: 12 }}>
                      {aiInsight.headline}
                    </Text>

                    {Array.isArray(aiInsight.patterns) && aiInsight.patterns.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <Text strong>Patterns</Text>
                        <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                          {aiInsight.patterns.map((p, i) => (
                            <li key={i} style={{ marginBottom: 6 }}>
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {aiInsight.likelyImpact && (
                      <div>
                        <Text strong>Likely Impact</Text>
                        <p style={{ marginTop: 6, marginBottom: 0 }}>{aiInsight.likelyImpact}</p>
                      </div>
                    )}
                  </Card>

                  {Array.isArray(aiInsight.flags) && aiInsight.flags.length > 0 && (
                    <>
                      <Text strong>Flags</Text>
                      <Table
                        className="custom-table"
                        style={{ marginTop: 8 }}
                        size="small"
                        columns={flagColumns}
                        dataSource={aiInsight.flags.map((f, i) => ({ ...f, key: i }))}
                        pagination={false}
                        scroll={{ x: 700 }}
                      />
                    </>
                  )}
                </>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message={insights.message || "AI insights are not available right now."}
                />
              )}
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card title="Team Attendance" className="card-professional" style={{ margin: 16 }}>
        <Tabs activeKey={activeTab} onChange={onTabChange} items={tabItems} />
      </Card>

      <Drawer
        title={selectedEmp ? `${selectedEmp.fullName} - Attendance` : "Attendance"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        destroyOnClose
      >
        {empLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : selectedEmp ? (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Employee">{selectedEmp.fullName}</Descriptions.Item>
              <Descriptions.Item label="Department">
                {selectedEmp.departmentName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Job Title">
                {selectedEmp.jobTitleName || "-"}
              </Descriptions.Item>
              {empWlb && (
                <>
                  <Descriptions.Item label="Worked / Expected (month)">
                    {empWlb.workedHours ?? "-"} / {empWlb.expectedHours ?? "-"} hrs
                  </Descriptions.Item>
                  <Descriptions.Item label="Balance">
                    {wlbFromDiff(wlbDiff(empWlb))}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            <Text strong>This month</Text>
            <Table
              size="small"
              style={{ marginTop: 8 }}
              rowKey={(r) => r.id || r.date}
              columns={[
                { title: "Date", key: "d", render: (_, r) => moment(r.date).format("DD MMM") },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "s",
                  render: (v) => attendanceStatusTag(v),
                },
                { title: "In", key: "in", render: (_, r) => fmtTime(r.firstCheckIn) },
                { title: "Out", key: "out", render: (_, r) => fmtTime(r.lastCheckOut) },
                {
                  title: "Worked",
                  dataIndex: "totalWorkedHours",
                  key: "w",
                  render: (v) => fmtWorked(v),
                },
              ]}
              dataSource={empRecords?.records || []}
              pagination={{ pageSize: 8 }}
              locale={{ emptyText: "No records this month" }}
            />
          </>
        ) : (
          <Empty />
        )}
      </Drawer>
    </DashboardLayout>
  );
};

export default TeamAttendance;
